// v2.19.22d — OPS B2B shell + auth Bearer + Personnalités CRUD.
// Sidebar 10 onglets (1 fonctionnel : Personnalités, 9 stubs).
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DirectoryCrudTab from "@/pages/DirectoryCrudTab";
import CoverageMapTab from "@/pages/CoverageMapTab";
import {
  COMMUNES_CIV,
  SHOPPING_CATS, SHOPPING_SCHEMA,
  FINANCE_CATS, FINANCE_SCHEMA,
  ADMIN_CATS, ADMIN_SCHEMA,
  HEALTH_CATS, HEALTH_SCHEMA,
  JUSTICE_CATS, JUSTICE_SCHEMA,
  COMMERCES_CATS, COMMERCES_SCHEMA,
  SPONSORED_CATS, SPONSORED_SCHEMA,
  EVENTS_CATS, EVENTS_SCHEMA,
  PARTNERS_CATS, PARTNERS_SCHEMA,
} from "@/pages/directorySchemas";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LS_KEY = "civ_ops_b2b_token";

const TABS = [
  { id: "personalities", emoji: "🌟", label: "Personnalités", functional: true },
  { id: "commerces", emoji: "🏪", label: "Commerces", functional: true },
  { id: "sante", emoji: "⚕", label: "Santé", functional: true },
  { id: "justice", emoji: "⚖", label: "Justice", functional: true },
  { id: "finance", emoji: "💰", label: "Finance", functional: true },
  { id: "administration", emoji: "🏛", label: "Administration", functional: true },
  { id: "shopping", emoji: "🛍", label: "Shopping", functional: true },
  { id: "sponsored", emoji: "📢", label: "Sponsored & Recos", functional: true },
  { id: "events", emoji: "🎉", label: "Events Live", functional: true },
  { id: "partners", emoji: "🤝", label: "Partenaires", functional: true },
  // v2.19.25 — Carte de Couverture Nationale (113 dpts GADM L3).
  { id: "coverage", emoji: "🗺", label: "Carte de Couverture", functional: true },
];

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

// ─── Login modal ────────────────────────────────────────────────────────
function TokenPrompt({ onValid }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const t = value.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ops-b2b/me`, { headers: authHeaders(t) });
      if (r.ok) {
        localStorage.setItem(LS_KEY, t);
        onValid(t);
        toast.success("Connexion OPS B2B réussie");
      } else {
        toast.error("Token invalide");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="opsb2b-overlay" data-testid="opsb2b-login-overlay">
      <div className="opsb2b-login-card">
        <div className="opsb2b-login-title">🏢 OPS B2B</div>
        <div className="opsb2b-login-sub">Accès réservé équipe interne.</div>
        <input
          data-testid="opsb2b-token-input"
          type="password"
          placeholder="CIV-OPS-B2B-XXXX-XXXX"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="opsb2b-input"
          autoFocus
        />
        <button
          data-testid="opsb2b-token-validate-btn"
          disabled={busy || !value.trim()}
          onClick={submit}
          className="opsb2b-btn-primary"
        >
          {busy ? "Vérification…" : "Valider"}
        </button>
        <div className="opsb2b-login-hint">
          Token démo dispo dans <code>backend-node/.env</code> (OPS_B2B_TOKENS).
        </div>
      </div>
    </div>
  );
}

// ─── Personality modal (create / edit) ──────────────────────────────────
const EMPTY_FORM = {
  id: "",
  nom: "",
  role: "",
  pays: "CI",
  categorie: "politique",
  annee_naissance: "",
  annee_fin: "",
  parti: "",
  ethnie: "",
  tags: "",
  biographie: "",
  bilan: "",
  heritage: "",
  photo_url: "",
  enabled: true,
};

function PersonalityModal({ token, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY_FORM;
    return {
      ...EMPTY_FORM,
      ...initial,
      tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
      annee_naissance: initial.annee_naissance ?? "",
      annee_fin: initial.annee_fin ?? "",
    };
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.nom.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      nom: form.nom.trim(),
      tags: form.tags
        ? String(form.tags)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
      annee_naissance: form.annee_naissance ? Number(form.annee_naissance) : undefined,
      annee_fin: form.annee_fin ? Number(form.annee_fin) : undefined,
    };
    if (!isEdit) delete payload.id; // server génère
    try {
      const url = isEdit
        ? `${API}/api/ops-b2b/personalities/${encodeURIComponent(form.id)}`
        : `${API}/api/ops-b2b/personalities`;
      const r = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.error || `Erreur ${r.status}`);
        return;
      }
      toast.success(isEdit ? "Personnalité mise à jour" : "Personnalité créée");
      onSaved(data.personality);
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="opsb2b-overlay" data-testid="opsb2b-personality-modal">
      <div className="opsb2b-modal">
        <div className="opsb2b-modal-header">
          <div className="opsb2b-modal-title">
            {isEdit ? "✏️ Éditer la personnalité" : "+ Nouvelle personnalité"}
          </div>
          <button
            data-testid="opsb2b-modal-close-btn"
            onClick={onClose}
            className="opsb2b-icon-btn"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="opsb2b-modal-body">
          {isEdit && (
            <div className="opsb2b-field">
              <label>ID</label>
              <input value={form.id} readOnly className="opsb2b-input opsb2b-readonly" />
            </div>
          )}
          <div className="opsb2b-row">
            <div className="opsb2b-field">
              <label>Nom *</label>
              <input
                data-testid="opsb2b-field-nom"
                value={form.nom}
                onChange={update("nom")}
                className="opsb2b-input"
                placeholder="Alassane Ouattara"
              />
            </div>
            <div className="opsb2b-field">
              <label>Rôle</label>
              <input
                value={form.role}
                onChange={update("role")}
                className="opsb2b-input"
                placeholder="Président de la République"
              />
            </div>
          </div>
          <div className="opsb2b-row">
            <div className="opsb2b-field">
              <label>Catégorie</label>
              <select
                value={form.categorie}
                onChange={update("categorie")}
                className="opsb2b-input"
              >
                <option value="politique">politique</option>
                <option value="culture">culture</option>
                <option value="histoire">histoire</option>
                <option value="sport">sport</option>
                <option value="autre">autre</option>
              </select>
            </div>
            <div className="opsb2b-field">
              <label>Pays</label>
              <input value={form.pays} onChange={update("pays")} className="opsb2b-input" />
            </div>
          </div>
          <div className="opsb2b-row">
            <div className="opsb2b-field">
              <label>Année naissance</label>
              <input
                type="number"
                value={form.annee_naissance}
                onChange={update("annee_naissance")}
                className="opsb2b-input"
                placeholder="1942"
              />
            </div>
            <div className="opsb2b-field">
              <label>Année fin (si déc.)</label>
              <input
                type="number"
                value={form.annee_fin}
                onChange={update("annee_fin")}
                className="opsb2b-input"
                placeholder="2023"
              />
            </div>
          </div>
          <div className="opsb2b-row">
            <div className="opsb2b-field">
              <label>Parti</label>
              <input value={form.parti} onChange={update("parti")} className="opsb2b-input" />
            </div>
            <div className="opsb2b-field">
              <label>Ethnie</label>
              <input value={form.ethnie} onChange={update("ethnie")} className="opsb2b-input" />
            </div>
          </div>
          <div className="opsb2b-field">
            <label>Tags (séparés par virgules)</label>
            <input
              value={form.tags}
              onChange={update("tags")}
              className="opsb2b-input"
              placeholder="président, RHDP, économie"
            />
          </div>
          <div className="opsb2b-field">
            <label>Photo URL (CDN)</label>
            <input
              value={form.photo_url}
              onChange={update("photo_url")}
              className="opsb2b-input"
              placeholder="https://…"
            />
          </div>
          <div className="opsb2b-field">
            <label>Biographie</label>
            <textarea
              value={form.biographie}
              onChange={update("biographie")}
              className="opsb2b-textarea"
              rows={3}
            />
          </div>
          <div className="opsb2b-field">
            <label>Bilan</label>
            <textarea
              value={form.bilan}
              onChange={update("bilan")}
              className="opsb2b-textarea"
              rows={3}
            />
          </div>
          <div className="opsb2b-field">
            <label>Héritage</label>
            <textarea
              value={form.heritage}
              onChange={update("heritage")}
              className="opsb2b-textarea"
              rows={2}
            />
          </div>
        </div>
        <div className="opsb2b-modal-footer">
          <button onClick={onClose} className="opsb2b-btn-ghost" data-testid="opsb2b-modal-cancel">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="opsb2b-btn-primary"
            data-testid="opsb2b-modal-save"
          >
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Personality card ───────────────────────────────────────────────────
function PersonalityCard({ p, criticalIds, onEdit, onDelete, onToggle }) {
  const initials = (p.nom || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const isCritical = criticalIds.includes(p.id);
  const disabled = p.enabled === false;
  return (
    <div className={`opsb2b-card ${disabled ? "opsb2b-card-disabled" : ""}`} data-testid={`opsb2b-card-${p.id}`}>
      <div className="opsb2b-card-media">
        {p.photo_url || p.image_url ? (
          <img src={p.photo_url || p.image_url} alt={p.nom} loading="lazy" />
        ) : (
          <div className="opsb2b-card-initials">{initials}</div>
        )}
        {isCritical && <div className="opsb2b-badge opsb2b-badge-critical">⚠ Critique</div>}
        {p.verified && <div className="opsb2b-badge opsb2b-badge-verified">✓ Vérifié</div>}
        {disabled && <div className="opsb2b-badge opsb2b-badge-off">Désactivé</div>}
      </div>
      <div className="opsb2b-card-body">
        <div className="opsb2b-card-name">{p.nom}</div>
        <div className="opsb2b-card-role">{p.role || p.categorie || "—"}</div>
        {Array.isArray(p.tags) && p.tags.length > 0 && (
          <div className="opsb2b-card-tags">
            {p.tags.slice(0, 3).map((t, i) => (
              <span key={i} className="opsb2b-tag">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="opsb2b-card-actions">
        <button
          onClick={() => onEdit(p)}
          className="opsb2b-icon-btn"
          title="Éditer"
          data-testid={`opsb2b-edit-${p.id}`}
        >
          ✏️
        </button>
        <button
          onClick={() => onToggle(p)}
          className="opsb2b-icon-btn"
          title={disabled ? "Activer" : "Désactiver"}
          data-testid={`opsb2b-toggle-${p.id}`}
        >
          👁
        </button>
        <button
          onClick={() => onDelete(p)}
          className="opsb2b-icon-btn opsb2b-danger"
          title="Supprimer"
          data-testid={`opsb2b-delete-${p.id}`}
        >
          🗑
        </button>
      </div>
    </div>
  );
}

// ─── Personalities Tab ──────────────────────────────────────────────────
function PersonalitiesTab({ token }) {
  const [items, setItems] = useState([]);
  const [criticalIds, setCriticalIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ops-b2b/personalities`, { headers: authHeaders(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.personalities || []);
      setCriticalIds(data.critical_ids || []);
    } catch (e) {
      toast.error("Impossible de charger les personnalités");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      [p.nom, p.role, p.parti, ...(p.tags || [])]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [items, search]);

  const handleEdit = (p) => {
    setEditing(p);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleDelete = async (p) => {
    if (criticalIds.includes(p.id)) {
      toast.error("Personnalité critique, désactive-la plutôt avec le toggle 👁");
      return;
    }
    if (!window.confirm(`Tu es sûr ? "${p.nom}" ne sera plus accessible par le LLM.`)) return;
    try {
      const r = await fetch(`${API}/api/ops-b2b/personalities/${encodeURIComponent(p.id)}`, {
        method: "DELETE",
        headers: authHeaders(token),
      });
      if (r.status === 204) {
        toast.success("Supprimée");
        reload();
      } else {
        const data = await r.json().catch(() => ({}));
        toast.error(data.message || data.error || `Erreur ${r.status}`);
      }
    } catch (e) {
      toast.error("Erreur réseau");
    }
  };

  const handleToggle = async (p) => {
    try {
      const r = await fetch(
        `${API}/api/ops-b2b/personalities/${encodeURIComponent(p.id)}/toggle-enabled`,
        { method: "POST", headers: authHeaders(token) }
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.error || "Erreur");
        return;
      }
      toast.success(data.enabled ? "Activée" : "Désactivée");
      reload();
    } catch (e) {
      toast.error("Erreur réseau");
    }
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditing(null);
    reload();
  };

  return (
    <div className="opsb2b-tab">
      <div className="opsb2b-tab-header">
        <div>
          <div className="opsb2b-tab-title">🌟 Personnalités</div>
          <div className="opsb2b-tab-sub">
            {loading ? "Chargement…" : `${filtered.length} / ${items.length} entrée(s)`}
          </div>
        </div>
        <div className="opsb2b-tab-actions">
          <input
            data-testid="opsb2b-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="opsb2b-input opsb2b-search"
          />
          <button onClick={handleCreate} className="opsb2b-btn-primary" data-testid="opsb2b-create-btn">
            + Nouvelle personnalité
          </button>
        </div>
      </div>
      {loading ? (
        <div className="opsb2b-empty">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="opsb2b-empty">Aucune personnalité.</div>
      ) : (
        <div className="opsb2b-grid">
          {filtered.map((p) => (
            <PersonalityCard
              key={p.id}
              p={p}
              criticalIds={criticalIds}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
      {modalOpen && (
        <PersonalityModal
          token={token}
          initial={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ─── Stub tab ───────────────────────────────────────────────────────────
function StubTab({ tab }) {
  return (
    <div className="opsb2b-tab">
      <div className="opsb2b-tab-header">
        <div>
          <div className="opsb2b-tab-title">
            {tab.emoji} {tab.label}
          </div>
          <div className="opsb2b-tab-sub">🚧 Bientôt disponible — phase v2.19.22e+</div>
        </div>
      </div>
      <div className="opsb2b-empty">Module en cours de développement.</div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export default function OpsB2bPage() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [validated, setValidated] = useState(false);
  const [active, setActive] = useState("personalities");

  // v2.19.38 — Redirect immédiat si user JWT role=commerce : /ops-b2b n'est plus accessible aux commerçants.
  // Leur dashboard est /mon-compte?tab=pro depuis ce sprint.
  useEffect(() => {
    try {
      const userTok = localStorage.getItem("civ_user_token");
      if (!userTok) return;
      const payload = JSON.parse(atob(userTok.split(".")[1] || ""));
      if (payload?.role === "commerce") {
        window.location.replace("/mon-compte?tab=pro");
      }
    } catch (_) { /* JWT malformé : on ignore et laisse le flow token statique */ }
  }, []);

  // Auto-validate token at mount.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/ops-b2b/me`, { headers: authHeaders(token) });
        if (cancelled) return;
        if (r.ok) setValidated(true);
        else {
          localStorage.removeItem(LS_KEY);
          setToken("");
        }
      } catch (e) {
        // garde le token, retry au clic
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setValidated(false);
    toast.info("Déconnecté");
  };

  if (!token || !validated) {
    return (
      <TokenPrompt
        onValid={(t) => {
          setToken(t);
          setValidated(true);
        }}
      />
    );
  }

  const activeTab = TABS.find((t) => t.id === active) || TABS[0];

  return (
    <div className="opsb2b-page" data-testid="opsb2b-page">
      <header className="opsb2b-header">
        <div className="opsb2b-brand">🏢 OPS B2B</div>
        <div className="opsb2b-header-actions">
          <Link to="/" className="opsb2b-btn-ghost">← Retour chat</Link>
          <button onClick={handleLogout} className="opsb2b-btn-ghost" data-testid="opsb2b-logout-btn">
            Déconnexion
          </button>
        </div>
      </header>
      <div className="opsb2b-shell">
        <aside className="opsb2b-sidebar" data-testid="opsb2b-sidebar">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActive(tab.id)}
              className={`opsb2b-sidebar-item ${active === tab.id ? "opsb2b-sidebar-active" : ""}`}
              data-testid={`opsb2b-tab-${tab.id}`}
            >
              <span className="opsb2b-sidebar-emoji">{tab.emoji}</span>
              <span className="opsb2b-sidebar-label">{tab.label}</span>
              {!tab.functional && <span className="opsb2b-sidebar-soon">soon</span>}
            </button>
          ))}
        </aside>
        <main className="opsb2b-main">
          {active === "personalities" && <PersonalitiesTab token={token} />}
          {active === "shopping" && (
            <DirectoryCrudTab
              domain="shopping"
              label="Shopping"
              emoji="🛍"
              schema={SHOPPING_SCHEMA}
              primaryFields={["name", "category_id", "commune"]}
              categoryField="category_id"
              communeField="commune"
              categoryOptions={SHOPPING_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "finance" && (
            <DirectoryCrudTab
              domain="finance"
              label="Finance"
              emoji="💰"
              schema={FINANCE_SCHEMA}
              primaryFields={["name", "category", "commune_name"]}
              categoryField="category"
              communeField="commune_name"
              categoryOptions={FINANCE_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "administration" && (
            <DirectoryCrudTab
              domain="administration"
              label="Administration"
              emoji="🏛"
              schema={ADMIN_SCHEMA}
              primaryFields={["service_name", "category", "commune_name"]}
              categoryField="category"
              communeField="commune_name"
              categoryOptions={ADMIN_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "sante" && (
            <DirectoryCrudTab
              domain="health"
              label="Santé"
              emoji="⚕"
              schema={HEALTH_SCHEMA}
              primaryFields={["nom", "category_id", "commune"]}
              categoryField="category_id"
              communeField="commune"
              categoryOptions={HEALTH_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "justice" && (
            <DirectoryCrudTab
              domain="justice"
              label="Justice"
              emoji="⚖"
              schema={JUSTICE_SCHEMA}
              primaryFields={["nom", "category_id", "commune"]}
              categoryField="category_id"
              communeField="commune"
              categoryOptions={JUSTICE_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "commerces" && (
            <DirectoryCrudTab
              domain="commerces"
              label="Commerces"
              emoji="🏪"
              schema={COMMERCES_SCHEMA}
              primaryFields={["nom", "categorie", "commune"]}
              categoryField="categorie"
              communeField="commune"
              categoryOptions={COMMERCES_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "sponsored" && (
            <DirectoryCrudTab
              domain="sponsored"
              label="Sponsored & Recos"
              emoji="📢"
              schema={SPONSORED_SCHEMA}
              primaryFields={["nom", "categorie", "commune"]}
              categoryField="categorie"
              communeField="commune"
              categoryOptions={SPONSORED_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "events" && (
            <DirectoryCrudTab
              domain="events"
              label="Events Live"
              emoji="🎉"
              schema={EVENTS_SCHEMA}
              primaryFields={["title", "sub_category", "commune"]}
              categoryField="sub_category"
              communeField="commune"
              categoryOptions={EVENTS_CATS}
              communeOptions={COMMUNES_CIV}
              token={token}
            />
          )}
          {active === "partners" && (
            <DirectoryCrudTab
              domain="partners"
              label="Partenaires"
              emoji="🤝"
              schema={PARTNERS_SCHEMA}
              primaryFields={["name", "category", "domain"]}
              categoryField="category"
              categoryOptions={PARTNERS_CATS}
              token={token}
            />
          )}
          {active === "coverage" && <CoverageMapTab />}
          {!activeTab.functional && <StubTab tab={activeTab} />}
        </main>
      </div>
    </div>
  );
}
